import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { AdminLog } from '@/domain/types/admin-log';
import Button from '@/presentation/components/button';
import Spinner from '@/presentation/components/spinner';
import Table from '@/presentation/components/table';
import {
  collection,
  DocumentData,
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { FaArrowDown, FaFilter } from 'react-icons/fa';
import { columnsAdminLogs } from './columns';

const AdminLogsPage = () => {
  const [data, setData] = useState<AdminLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastVisible, setLastVisible] =
    useState<DocumentSnapshot<DocumentData> | null>(null);
  const [limitRows, setLimitRows] = useState(50);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [collectionFilter, setCollectionFilter] = useState<string>('all');

  const fetchData = useCallback(
    async (lastVisibleProp: DocumentSnapshot<DocumentData> | null = null) => {
      setIsLoading(true);

      try {
        const collectionRef = collection(db, DOCUMENTS_DB.ADMIN_LOGS);

        let queryConstraints: any[] = [
          orderBy('timestamp', 'desc'),
          limit(limitRows),
        ];

        if (actionFilter !== 'all') {
          queryConstraints.splice(0, 0, where('action', '==', actionFilter));
        }

        if (collectionFilter !== 'all') {
          queryConstraints.splice(
            actionFilter !== 'all' ? 1 : 0,
            0,
            where('collection', '==', collectionFilter),
          );
        }

        if (lastVisibleProp) {
          queryConstraints.push(startAfter(lastVisibleProp));
        }

        const queryRef = query(collectionRef, ...queryConstraints);
        const snapshot = await getDocs(queryRef);

        const newData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp:
              data.timestamp instanceof Timestamp
                ? data.timestamp.toDate()
                : new Date(data.timestamp),
          } as AdminLog;
        });

        if (lastVisibleProp) {
          setData((prev) => [...prev, ...newData]);
        } else {
          setData(newData);
        }

        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } catch (error) {
        console.error('Error fetching admin logs:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [limitRows, actionFilter, collectionFilter],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = () => {
    setData([]);
    setLastVisible(null);
    fetchData();
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Logs de Administradores</h1>
      </div>

      {/* Filtros */}
      <div className="flex justify-between items-center gap-4 bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-500" />
            <span className="text-sm text-gray-500">Filtros:</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Acción:</span>
            <select
              className="border border-gray-300 bg-white rounded-md p-2 text-sm"
              onChange={(e) => setActionFilter(e.target.value)}
              value={actionFilter}
            >
              <option value="all">Todas</option>
              <option value="CREATE">Crear</option>
              <option value="UPDATE">Actualizar</option>
              <option value="DELETE">Eliminar</option>
              <option value="RELEASE_TO_ALL">Liberación a todos</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Colección:</span>
            <select
              className="border border-gray-300 bg-white rounded-md p-2 text-sm"
              onChange={(e) => setCollectionFilter(e.target.value)}
              value={collectionFilter}
            >
              <option value="all">Todas</option>
              <option value={DOCUMENTS_DB.USERS}>Usuarios</option>
              <option value={DOCUMENTS_DB.RELEASES}>Últimas Horas</option>
              <option value={DOCUMENTS_DB.NEWSLETTERS}>Boletines</option>
              <option value={DOCUMENTS_DB.POLICIES}>Políticas</option>
              <option value={DOCUMENTS_DB.REPORTS}>Reportes</option>
              <option value={DOCUMENTS_DB.CRITICAL_STANDARS}>
                Estándares Críticos
              </option>
              <option value={DOCUMENTS_DB.PODCASTS}>Podcasts</option>
              <option value={DOCUMENTS_DB.VIDEOS}>Videos</option>
            </select>
          </div>

          <Button
            label="Aplicar Filtros"
            variant="secondary"
            onClick={handleFilterChange}
            className="text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Mostrar:</span>
          <select
            className="border border-gray-300 bg-white rounded-md p-2"
            onChange={(e) => setLimitRows(Number(e.target.value))}
            value={limitRows}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        ) : (
          <>
            {data.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No hay logs disponibles
                </h3>
                <p className="text-gray-500">
                  No se encontraron registros de actividad con los filtros
                  seleccionados
                </p>
              </div>
            ) : (
              <>
                <Table columns={columnsAdminLogs} data={data} />
                <div className="flex justify-center pt-4">
                  <Button
                    label="Mostrar más"
                    variant="ghost"
                    icon={<FaArrowDown />}
                    positionIcon="right"
                    onClick={() => fetchData(lastVisible)}
                    className="w-fit"
                    disabled={isLoading}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLogsPage;
